/**
 * The day's numbers: a ring for calories and a bar for each macro.
 */
import type { Day } from "../lib/api";
import { progress } from "../lib/creature";
import { DAILY_TARGETS, NUTRIENTS, NUTRIENT_GROUPS } from "../../server/nutrients";

/** How much of the goal this is, as a whole number. It can go past 100. */
function percentOf(value: number, goal: number): number {
  return goal > 0 ? Math.round((Math.max(0, value) / goal) * 100) : 0;
}

/** The calorie ring. An SVG circle with a gap in it, sized to the progress. */
function Ring({ value, goal }: { value: number; goal: number }) {
  const size = 148;
  const stroke = 13;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = progress(value, goal) * circumference;
  const over = value > goal;

  return (
    <div className="ring-wrap">
      <svg className="ring" width={size} height={size} aria-hidden="true">
        <circle
          className="ring__track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className={`ring__value${over ? " ring__over" : ""}`}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          // The dash is as long as the filled part, and the gap is the rest of
          // the circle -- which is how a circle becomes a progress ring.
          strokeDasharray={`${filled} ${circumference}`}
        />
      </svg>
      <div className="ring-centre">
        <b>{Math.round(value)}</b>
        <span className="muted small">of {Math.round(goal)} kcal</span>
        <span className="ring-percent">{percentOf(value, goal)}%</span>
      </div>
    </div>
  );
}

function Macro({
  label,
  value,
  goal,
  colour,
}: {
  label: string;
  value: number;
  goal: number;
  colour: string;
}) {
  return (
    <div className="macro">
      <span className="muted small">{label}</span>
      <b>{Math.round(value)}g</b>
      <span className="muted small">of {Math.round(goal)}g</span>
      <div className="macro-bar">
        <span style={{ width: `${progress(value, goal) * 100}%`, background: colour }} />
      </div>
      <span className="macro-percent">{percentOf(value, goal)}%</span>
    </div>
  );
}

/** Small amounts keep their decimals; 0.4mg of B6 shouldn't read as nothing. */
function amountText(value: number): string {
  const places = value < 1 ? 2 : value < 10 ? 1 : 0;
  return String(Math.round(value * 10 ** places) / 10 ** places);
}

/**
 * Everything else, folded away until asked for. Every nutrient with a daily
 * amount is always listed, so it is clear what is still to get. One with no
 * daily amount only appears once something logged has listed it, because until
 * then there is nothing to say about it.
 */
function FullNutrition({ day }: { day: Day }) {
  const food = day.entries.filter((entry) => entry.kind !== "exercise");

  return (
    <details className="full-nutrition">
      <summary>Full nutrition</summary>
      {NUTRIENT_GROUPS.map((group) => {
        const rows = NUTRIENTS.filter((n) => n.group === group).map((n) => ({
          ...n,
          target: DAILY_TARGETS[n.key],
          logged: food.some((entry) => entry[n.key] != null),
        }));
        const shown = rows.filter((n) => n.target || n.logged);
        if (shown.length === 0) return null;
        return (
          <div key={group}>
            <h3 className="nutrient-group">{group}</h3>
            {shown.map((n) => {
              const amount = day.totals[n.key];
              const over = Boolean(n.target?.limit) && amount > (n.target?.amount ?? 0);
              return (
                <div className="nutrient-row" key={n.key}>
                  <div className="nutrient-row__top">
                    <span>{n.label}</span>
                    {n.target && (
                      <b className={over ? "nutrient-over" : undefined}>
                        {percentOf(amount, n.target.amount)}%
                      </b>
                    )}
                  </div>
                  <div className="muted small">
                    {amountText(amount)} {n.unit}
                    {n.target
                      ? ` of ${n.target.limit ? "at most " : ""}${amountText(n.target.amount)} ${n.unit}`
                      : " · no set daily amount"}
                  </div>
                  {n.target && (
                    <div className="macro-bar nutrient-bar">
                      <span
                        style={{
                          width: `${progress(amount, n.target.amount) * 100}%`,
                          background: over ? "var(--warn)" : "var(--accent)",
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      <p className="muted small" style={{ marginBottom: 0 }}>
        Daily amounts are the FDA's Daily Values for adults. Each total only counts
        foods that listed that nutrient, so the real amount may be higher.
      </p>
    </details>
  );
}

export function Totals({ day }: { day: Day }) {
  const { totals, settings, burned, net } = day;

  return (
    <div className="card">
      <Ring value={net} goal={settings.goal_calories} />

      {burned > 0 && (
        <p className="muted small" style={{ textAlign: "center", margin: "10px 0 0" }}>
          {Math.round(totals.calories)} eaten − {Math.round(burned)} burned
        </p>
      )}

      <div className="macros">
        <Macro label="Protein" value={totals.protein_g} goal={settings.goal_protein_g} colour="var(--protein)" />
        <Macro label="Carbs" value={totals.carbs_g} goal={settings.goal_carbs_g} colour="var(--carbs)" />
        <Macro label="Fat" value={totals.fat_g} goal={settings.goal_fat_g} colour="var(--fat)" />
      </div>

      <FullNutrition day={day} />
    </div>
  );
}
